import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';

import { useApplicationColumns } from '../components/application-columns';
import { ApplicationStatusTabs } from '../components/application-status-tabs';
import { useApplications } from '../hooks/use-applications';
import { statusTabOf } from '../schemas/application-list-search';
import type { StatusTab } from '../schemas/application-rules';

const ROUTE_ID = '/_authed/applications/' as const;
const ROUTE_PATH = '/applications/' as const;

function ApplicationsList() {
  const { t } = useTranslation('applications');
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const status = statusTabOf(search);

  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange });
  const params = useMemo(() => ({ ...list.params, status }), [list.params, status]);
  const query = useApplications(params);
  const columns = useApplicationColumns();
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    getRowId: (row) => row.id,
    tableId: 'applications',
  });

  const onTabChange = (tab: StatusTab) =>
    void navigate({
      search: (prev) => ({ ...prev, status: tab, cursor: undefined }),
      replace: true,
    });

  return (
    <PageContent>
      <PageHeader
        title={<span className="font-serif text-[1.875rem] font-normal">{t('title')}</span>}
        description={t('description')}
      />
      <ApplicationStatusTabs value={status} counts={query.data?.counts} onChange={onTabChange} />
      <DataTable
        table={table}
        search={{ placeholder: t('search'), label: t('search') }}
        loading={query.isPending}
        fetching={query.isFetching}
        error={query.error}
        onRetry={() => void query.refetch()}
        onRowClick={(row) =>
          void navigate({ to: '/applications/$applicationId', params: { applicationId: row.id } })
        }
        getRowLabel={(row) => row.brandName}
        resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
        emptyState={{ title: t(`empty.${status}`) }}
        tour={{ table: 'platform.applications.table', search: 'platform.applications.search' }}
      />
    </PageContent>
  );
}

export function ApplicationsListPage() {
  return (
    <FeatureErrorBoundary name="applications.list">
      <ApplicationsList />
    </FeatureErrorBoundary>
  );
}
